import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CalendarClock, Plus, Loader2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const STATUS_LABEL = {
  proposed: { text: "Bekliyor", cls: "bg-warning/10 text-warning" },
  accepted: { text: "Kabul edildi", cls: "bg-success/10 text-success" },
  rejected: { text: "Reddedildi", cls: "bg-destructive/10 text-destructive" },
  rescheduled: { text: "Yeni saat önerildi", cls: "bg-primary/10 text-primary" },
  cancelled: { text: "İptal", cls: "bg-muted text-muted-foreground" },
  completed: { text: "Tamamlandı", cls: "bg-accent/10 text-accent" },
};

export default function SchoolMeetings() {
  const [meetings, setMeetings] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [invId, setInvId] = useState("");
  const [slots, setSlots] = useState([{ start: "", end: "", location: "", meeting_type: "in_person" }]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, invs] = await Promise.all([
        api.get(`${V1}/meetings/mine`),
        api.get(`${V1}/invitations/mine`).catch(() => ({ data: [] })),
      ]);
      setMeetings(m.data || []);
      setInvitations(invs.data || []);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addSlot = () => setSlots((s) => [...s, { start: "", end: "", location: "", meeting_type: "in_person" }]);
  const setSlot = (i, k, v) => setSlots((s) => s.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const removeSlot = (i) => setSlots((s) => s.filter((_, idx) => idx !== i));

  const propose = async () => {
    if (!invId) { toast.error("Bir davet seçin"); return; }
    const valid = slots.filter((s) => s.start && s.end);
    if (!valid.length) { toast.error("En az bir saat aralığı girin"); return; }
    setBusy(true);
    try {
      await api.post(`${V1}/invitations/${invId}/meetings`, { slots: valid, message });
      toast.success("Görüşme önerildi, veliye bildirim gönderildi");
      setOpen(false);
      setInvId(""); setSlots([{ start: "", end: "", location: "", meeting_type: "in_person" }]); setMessage("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Bu görüşmeyi iptal etmek istiyor musun?")) return;
    try {
      await api.post(`${V1}/meetings/${id}/cancel`);
      toast.success("İptal edildi");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  if (loading) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="school-meetings-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><CalendarClock className="h-7 w-7 text-primary" /> Görüşme Planlama</h1>
          <p className="text-muted-foreground text-sm mt-1">Burslu davetlerinizde ailelerle mülakat/randevu saatleri önerin. Aileler paneli üzerinden onaylar, reddeder veya yeni saat önerir.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="propose-meeting-btn"><Plus className="h-4 w-4" /> Görüşme Öner</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Yeni görüşme önerisi</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Davet</label>
                <select className="w-full p-2 rounded border border-border bg-background text-sm mt-1" value={invId} onChange={(e) => setInvId(e.target.value)} data-testid="meeting-invitation-select">
                  <option value="">— Davet seçin —</option>
                  {invitations.map((iv) => (
                    <option key={iv.id} value={iv.id}>
                      {iv.child?.name || iv.child_id?.slice(0, 8)} {iv.child?.surname || ""} · %{iv.scholarship_rate} burs
                    </option>
                  ))}
                </select>
              </div>
              {slots.map((s, i) => (
                <div key={i} className="p-3 rounded border border-border bg-background/50 space-y-2">
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>Alternatif #{i + 1}</span>
                    {slots.length > 1 && <button className="text-destructive" onClick={() => removeSlot(i)}><X className="h-3 w-3" /></button>}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="datetime-local" value={s.start} onChange={(e) => setSlot(i, "start", e.target.value)} data-testid={`meeting-start-${i}`} />
                    <Input type="datetime-local" value={s.end} onChange={(e) => setSlot(i, "end", e.target.value)} data-testid={`meeting-end-${i}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Yer / adres" value={s.location} onChange={(e) => setSlot(i, "location", e.target.value)} data-testid={`meeting-location-${i}`} />
                    <select className="p-2 rounded border border-border bg-background text-sm" value={s.meeting_type} onChange={(e) => setSlot(i, "meeting_type", e.target.value)} data-testid={`meeting-type-${i}`}>
                      <option value="in_person">Yüz yüze</option>
                      <option value="video">Online</option>
                      <option value="phone">Telefon</option>
                    </select>
                  </div>
                </div>
              ))}
              {slots.length < 5 && (
                <Button variant="outline" size="sm" onClick={addSlot} data-testid="meeting-add-slot"><Plus className="h-3 w-3" /> Alternatif ekle</Button>
              )}
              <Textarea placeholder="Aileye mesaj (opsiyonel)" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="meeting-message" />
              <Button className="w-full" onClick={propose} disabled={busy} data-testid="meeting-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Öneri Gönder
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {meetings.length === 0 && (
          <div className="p-10 rounded-xl border border-dashed border-border text-center text-muted-foreground">
            Henüz planlanmış görüşme yok. Bir davete görüşme önerin.
          </div>
        )}
        {meetings.map((m) => {
          const st = STATUS_LABEL[m.status] || { text: m.status, cls: "bg-muted" };
          return (
            <div key={m.id} className="p-4 rounded-xl border border-border bg-card" data-testid={`meeting-item-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium">{m.child_name || "Aile"}</div>
                    <span className={`text-xs px-2 py-0.5 rounded ${st.cls}`}>{st.text}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{m.proposed_slots?.length || 0} alternatif · {new Date(m.created_at).toLocaleString("tr-TR")}</div>
                  {m.selected_slot && (
                    <div className="text-xs mt-1 text-success">Onaylı: {new Date(m.selected_slot.start).toLocaleString("tr-TR")} — {m.selected_slot.location}</div>
                  )}
                </div>
                {["proposed", "rescheduled"].includes(m.status) && (
                  <Button variant="outline" size="sm" onClick={() => cancel(m.id)} data-testid={`meeting-cancel-${m.id}`}>İptal</Button>
                )}
              </div>
              <div className="mt-3 grid gap-2">
                {(m.proposed_slots || []).map((s, i) => (
                  <div key={i} className="text-xs p-2 rounded bg-muted/40">
                    <b>{new Date(s.start).toLocaleString("tr-TR")}</b> — {new Date(s.end).toLocaleString("tr-TR")} · {s.meeting_type === "video" ? "Online" : s.meeting_type === "phone" ? "Telefon" : "Yüz yüze"}
                    {s.location && <span> · {s.location}</span>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
